import React, { useEffect, useRef, useState } from 'react';

/* ────────────────────────────────────────────────────────────────────────
   TODO: PLACEHOLDER FIGURES — replace with real OLI Gardens impact numbers
   before launch. Update only this array; the component handles the rest.
   ──────────────────────────────────────────────────────────────────────── */
const DEFAULT_STATS = [
  { value: 250, suffix: '+', label: 'Raised Beds Donated' },
  { value: 2100, suffix: '+', label: 'Families Served' },
  { value: 18, suffix: '', label: 'Communities Reached' },
  { value: 10000, suffix: '+', label: 'Pounds of Produce Grown' },
];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** One number that counts 0 → value once `start` flips true. */
const CountUp = ({ value, suffix = '', start, duration = 1800 }) => {
  const [display, setDisplay] = useState(0);
  const frameRef = useRef(null);

  useEffect(() => {
    if (!start) return;
    if (prefersReducedMotion()) {
      setDisplay(value);
      return;
    }

    const t0 = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - t0) / duration, 1);
      // ease-out cubic so the count settles gently
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(eased * value));
      if (progress < 1) frameRef.current = requestAnimationFrame(tick);
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [start, value, duration]);

  return (
    <span>
      {display.toLocaleString()}
      {suffix}
    </span>
  );
};

/**
 * Impact stats bar — numbers count up when scrolled into view.
 *
 * Props:
 *   stats — optional override array of { value, suffix, label }
 *   dark  — render on the forest-green band (default true)
 */
export const ImpactStats = ({ stats = DEFAULT_STATS, dark = true }) => {
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className={dark ? 'bg-forest text-cream' : 'bg-cream text-forest'}
      aria-label="Our impact"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10 text-center">
          {stats.map((stat) => (
            <div key={stat.label}>
              <p className={`font-display font-bold text-4xl sm:text-5xl ${dark ? 'text-gold_soft' : 'text-gold'}`}>
                <CountUp value={stat.value} suffix={stat.suffix} start={inView} />
              </p>
              <p className={`oli-label mt-3 ${dark ? 'text-cream/80' : 'text-ink_soft'}`}>{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ImpactStats;
